import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowLeft,
  LuBox,
  LuBriefcaseBusiness,
  LuClipboardList,
  LuDownload,
  LuFileText,
  LuInfo,
  LuLock,
  LuMailQuestion,
  LuEllipsisVertical,
  LuPaperclip,
  LuSend,
  LuShield,
  LuTriangleAlert,
  LuUserRound,
  LuUsersRound,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  SUPPORT_STATUS_OPTIONS,
  fetchMyAssignedDisputeById,
  postMyDisputeMessage,
  supportDisputeExtractMessage,
  updateMyAssignedDisputeStatus,
} from "../../services/supportDispute.service";
import "../../assets/styles/support_disputes.scss";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function senderLabel(type) {
  if (type === "support") return "Support";
  if (type === "buyer") return "Buyer";
  if (type === "seller") return "Seller";
  return "System";
}

function summarizeText(value, max = 150) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}

function isImageAttachment(mime, name = "") {
  if (String(mime || "").toLowerCase().startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(String(name || ""));
}

export default function SupportDisputeDetailPage({
  fetchDetail = fetchMyAssignedDisputeById,
  updateStatus = updateMyAssignedDisputeStatus,
  postMessage = postMyDisputeMessage,
  reopenDispute = null,
  extractMessage = supportDisputeExtractMessage,
  backPath = "/dashboard/support/disputes",
  backLabel = "My Assigned Disputes",
}) {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [message, setMessage] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [showAllTimeline, setShowAllTimeline] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDetail(disputeId);
      if (!data?.dispute) throw new Error("Dispute not found.");
      setDetail(data);
      setStatus(data.dispute.status);
      setResolutionNotes(data.dispute.resolutionNotes || "");
      if (data.permissions?.canSendPublicMessages === false) setIsInternal(true);
    } catch (error) {
      toast.error(extractMessage(error, "Failed to load dispute."));
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  }, [backPath, disputeId, extractMessage, fetchDetail, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages]);

  // Light polling for new messages
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchDetail(disputeId)
          .then((data) => {
            if (data?.dispute) setDetail(data);
          })
          .catch(() => {});
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [disputeId, fetchDetail]);

  const onSaveStatus = async () => {
    if (!permissions.canUpdateStatus) {
      toast.error(permissions.statusRestrictionMessage || "You cannot update this dispute status.");
      return;
    }
    if (!status) {
      toast.error("Please select a status.");
      return;
    }
    setSavingStatus(true);
    try {
      await updateStatus(disputeId, {
        status,
        resolutionNotes:
          status === "resolved" || status === "closed" ? resolutionNotes : undefined,
      });
      toast.success("Status updated.");
      await load();
    } catch (error) {
      toast.error(extractMessage(error, "Failed to update status."));
    } finally {
      setSavingStatus(false);
    }
  };

  const onSendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim() && !attachment) {
      toast.error("Enter a message or attach a file.");
      return;
    }
    setSending(true);
    try {
      const sendAsInternal = permissions.canSendPublicMessages ? isInternal : true;
      await postMessage(disputeId, {
        message: message.trim(),
        isInternal: sendAsInternal,
        attachment,
      });
      setMessage("");
      setAttachment(null);
      setIsInternal(!permissions.canSendPublicMessages);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
      toast.success(sendAsInternal ? "Internal note added." : "Message sent.");
    } catch (error) {
      toast.error(extractMessage(error, "Failed to send message."));
    } finally {
      setSending(false);
    }
  };

  const onReopen = async () => {
    if (!reopenDispute) return;
    const reason = window.prompt("Reason for reopening this dispute:");
    if (reason === null) return;
    setSavingStatus(true);
    try {
      await reopenDispute(disputeId, reason);
      toast.success("Dispute reopened.");
      await load();
    } catch (error) {
      toast.error(extractMessage(error, "Failed to reopen dispute."));
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading || !detail?.dispute) {
    return (
      <div className="sd-page">
        <p className="sd-empty">Loading dispute details...</p>
      </div>
    );
  }

  const dispute = detail.dispute;
  const permissions = detail.permissions ?? {
    canUpdateStatus: true,
    canSendPublicMessages: true,
    canAddInternalNotes: true,
    statusRestrictionMessage: null,
  };
  const messages = Array.isArray(detail.messages) ? detail.messages : [];
  const timeline = Array.isArray(detail.timeline) ? detail.timeline : [];
  const visibleTimeline = showAllTimeline ? timeline : timeline.slice(0, 3);
  const attachments = [
    ...(dispute.attachmentPath || dispute.attachmentName
      ? [
          {
            id: `dispute-${dispute.id}`,
            name: dispute.attachmentName || "Dispute attachment",
            path: dispute.attachmentPath,
          },
        ]
      : []),
    ...messages
      .filter((item) => item.attachmentPath)
      .map((item) => ({
        id: `message-${item.id}`,
        name: item.attachmentName || "Message attachment",
        path: item.attachmentPath,
      })),
  ];

  const beginInternalNote = () => {
    setIsInternal(true);
    messageInputRef.current?.focus();
  };

  const requestMoreInfo = () => {
    setIsInternal(false);
    setMessage("Please provide additional information and supporting documents for this dispute.");
    messageInputRef.current?.focus();
  };

  const escalateDispute = () => {
    setStatus("under_investigation");
    toast.info("Status set to Under Investigation. Click Update Status to confirm.");
  };

  return (
    <div className="sd-page sd-detail-page">
      <nav className="sd-breadcrumb" aria-label="Breadcrumb">
        <Link to={backPath}>{backLabel}</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">{dispute.disputeNumber}</span>
      </nav>

      <header className="sd-detail-head">
        <div className="sd-detail-title-row">
          <button
            type="button"
            className="sd-icon-btn sd-icon-btn--ghost"
            onClick={() => navigate(backPath)}
            aria-label="Back"
          >
            <LuArrowLeft />
          </button>
          <div>
            <h1>{dispute.disputeNumber}</h1>
            <p>{dispute.subject}</p>
          </div>
        </div>
        <div className="sd-detail-badges">
          <span className={`sd-pill sd-pill-status sd-pill-status--${dispute.status}`}>
            {dispute.statusLabel}
          </span>
          <span className={`sd-pill sd-pill--${String(dispute.priority).toLowerCase()}`}>
            {dispute.priority}
          </span>
          <span className={dispute.slaOverdue ? "sd-sla sd-sla--overdue" : "sd-sla"}>
            SLA: {dispute.slaRemainingLabel}
          </span>
          <button type="button" className="sd-icon-btn sd-icon-btn--ghost" aria-label="More actions">
            <LuEllipsisVertical />
          </button>
        </div>
      </header>

      <div className="sd-detail-shell">
        <main className="sd-detail-main">
          <section className="sd-detail-overview">
            <article className="sd-card sd-info-card">
              <h2><LuClipboardList aria-hidden /> Dispute Summary</h2>
              <dl>
                <dt>Category</dt>
                <dd>{dispute.category}</dd>
                <dt>Type</dt>
                <dd>{dispute.disputeType}</dd>
                <dt>Reason for Dispute</dt>
                <dd className="sd-summary-reason" title={dispute.description || undefined}>
                  {summarizeText(dispute.description)}
                </dd>
                <dt>Submitted On</dt>
                <dd>{formatDateTime(dispute.createdAt)}</dd>
                <dt>Last Updated</dt>
                <dd>{formatDateTime(dispute.updatedAt)}</dd>
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2><LuBox aria-hidden /> Order Details</h2>
              <dl>
                <dt>Reference Type</dt>
                <dd>{dispute.referenceType || "—"}</dd>
                <dt>Reference No.</dt>
                <dd>{dispute.referenceNumber || "—"}</dd>
                <dt>Order Date</dt>
                <dd>{formatDateTime(dispute.orderDate)}</dd>
                <dt>Order Value</dt>
                <dd>
                  {dispute.orderValue != null
                    ? `${dispute.currency || ""} ${dispute.orderValue}`.trim()
                    : "—"}
                </dd>
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2><LuBriefcaseBusiness aria-hidden /> Assignment Information</h2>
              <dl>
                <dt>Assigned To</dt>
                <dd>{dispute.assignedToName || "—"}</dd>
                <dt>Assignment Type</dt>
                <dd>
                  {dispute.assignmentType === "manual"
                    ? "Manual"
                    : dispute.assignmentType === "automatic"
                      ? "Automatic (Round Robin)"
                      : "Unassigned"}
                </dd>
                <dt>Assigned At</dt>
                <dd>{formatDateTime(dispute.assignedAt)}</dd>
                <dt>SLA Due</dt>
                <dd>{formatDateTime(dispute.slaDueAt)}</dd>
              </dl>
            </article>
          </section>

          <section className="sd-party-grid">
            <article className="sd-card sd-info-card">
              <h2><LuUserRound aria-hidden /> Buyer Information</h2>
              <dl>
                <dt>Name</dt>
                <dd>{dispute.buyerName}</dd>
                <dt>Company</dt>
                <dd>{dispute.buyerCompany || "—"}</dd>
                <dt>Email</dt>
                <dd>{dispute.buyerEmail}</dd>
                <dt>Phone</dt>
                <dd>
                  {dispute.buyerCountryCode} {dispute.buyerMobile}
                </dd>
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2><LuUsersRound aria-hidden /> Seller Information</h2>
              <dl>
                <dt>Name</dt>
                <dd>{dispute.supplierName}</dd>
                <dt>Contact</dt>
                <dd>{dispute.supplierContact || "—"}</dd>
                <dt>Email</dt>
                <dd>{dispute.supplierEmail || "—"}</dd>
                <dt>Phone</dt>
                <dd>
                  {dispute.supplierCountryCode || ""} {dispute.supplierPhone || "—"}
                </dd>
                <dt>Address</dt>
                <dd className="sd-multiline">{dispute.supplierAddress || "—"}</dd>
              </dl>
            </article>
          </section>

          <section className="sd-card sd-conversation">
            <header className="sd-conversation__head">
              <div>
                Conversation
                <small>
                  {permissions.canSendPublicMessages
                    ? "Communicate with buyer and seller. All messages are recorded."
                    : "View the complete conversation and add internal notes."}
                </small>
              </div>
              {permissions.canSendPublicMessages ? (
                <label className="sd-internal-toggle">
                  <span>Internal Note</span>
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                  />
                  <i aria-hidden />
                </label>
              ) : (
                <span className="sd-internal-only">
                  <LuLock aria-hidden /> Internal notes only
                </span>
              )}
            </header>
              <div className={`sd-chat${messages.length === 0 ? " sd-chat--empty" : ""}`}>
                <div className="sd-chat__thread" aria-live="polite">
                  {messages.length === 0 ? (
                    <p className="sd-empty">No messages yet. Start the conversation below.</p>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`sd-chat__bubble sd-chat__bubble--${msg.senderType}${
                          msg.isInternal ? " is-internal" : ""
                        }`}
                      >
                        <header>
                          <strong>
                            {msg.senderName}{" "}
                            <span>({senderLabel(msg.senderType)})</span>
                          </strong>
                          {msg.isInternal ? (
                            <em>
                              <LuLock aria-hidden /> Internal
                            </em>
                          ) : null}
                          <time>{formatDateTime(msg.createdAt)}</time>
                        </header>
                        <p>{msg.messageBody}</p>
                        {msg.attachmentPath ? (
                          isImageAttachment(msg.attachmentMime, msg.attachmentName) ? (
                            <a
                              className="sd-chat__image"
                              href={resolvePublicFileUrl(msg.attachmentPath)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={resolvePublicFileUrl(msg.attachmentPath)}
                                alt={msg.attachmentName || "Conversation attachment"}
                              />
                            </a>
                          ) : (
                            <a
                              className="sd-chat__file"
                              href={resolvePublicFileUrl(msg.attachmentPath)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <LuPaperclip aria-hidden /> {msg.attachmentName || "Attachment"}
                            </a>
                          )
                        ) : null}
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                {permissions.canAddInternalNotes || permissions.canSendPublicMessages ? (
                <form className="sd-chat__composer" onSubmit={onSendMessage}>
                  {isInternal ? (
                    <div className="sd-internal-banner">
                      <LuShield aria-hidden /> Internal note — hidden from buyer and seller
                    </div>
                  ) : null}
                  <textarea
                    ref={messageInputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      isInternal
                        ? "Write an internal note for support / admin..."
                        : "Write a message to the Buyer and Seller..."
                    }
                    rows={3}
                  />
                  <div className="sd-chat__composer-actions">
                    {permissions.canSendPublicMessages ? (
                    <label className="sd-attach-action">
                      <LuPaperclip aria-hidden /> Attach File
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                      />
                    </label>
                    ) : null}
                    {attachment ? <small>{attachment.name}</small> : null}
                    <button type="submit" className="sd-btn sd-btn--primary" disabled={sending}>
                      <LuSend aria-hidden /> {sending ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
                ) : (
                  <div className="sd-permission-notice" role="note">
                    <LuLock aria-hidden />
                    <p>Conversation is read-only for this account.</p>
                  </div>
                )}
              </div>
          </section>

          <section className="sd-card sd-activity-card">
            <header>
              <h2>Activity Timeline</h2>
              {timeline.length > 3 ? (
                <button
                  type="button"
                  onClick={() => setShowAllTimeline((current) => !current)}
                  aria-expanded={showAllTimeline}
                >
                  {showAllTimeline ? "Show Less" : `View All (${timeline.length})`}
                </button>
              ) : null}
            </header>
              <ol className="sd-timeline">
                {timeline.length === 0 ? (
                  <li className="sd-empty">No activity recorded yet.</li>
                ) : (
                  visibleTimeline.map((item) => (
                    <li key={item.id}>
                      <div className="sd-timeline__dot" aria-hidden />
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.details || "—"}</p>
                        <small>
                          {item.actorName || item.actorType} · {formatDateTime(item.createdAt)}
                        </small>
                      </div>
                    </li>
                  ))
                )}
              </ol>
          </section>
        </main>

        <aside className="sd-detail-side">
          <section className="sd-card sd-status-card">
            <h2><LuUserRound aria-hidden /> Status Management</h2>
            {permissions.canUpdateStatus ? (
              <>
              <label className="sd-field">
                <span>Current Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {SUPPORT_STATUS_OPTIONS
                    .filter(
                      (opt) =>
                        !Array.isArray(permissions.allowedStatuses) ||
                        permissions.allowedStatuses.includes(opt.value),
                    )
                    .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                    ))}
                </select>
              </label>
                {(status === "resolved" || status === "closed") && (
                  <label className="sd-field">
                    <span>Resolution / Closure Notes</span>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      rows={4}
                      placeholder="Summarize the resolution or closure reason"
                    />
                  </label>
                )}
                <button
                  type="button"
                  className="sd-btn sd-btn--primary sd-btn--block"
                  disabled={savingStatus}
                  onClick={onSaveStatus}
                >
                  {savingStatus ? "Saving..." : "Update Status"}
                </button>
              </>
            ) : (
              <div className="sd-permission-notice" role="note">
                <LuLock aria-hidden />
                <p>{permissions.statusRestrictionMessage}</p>
                {permissions.canReopen &&
                ["resolved", "closed"].includes(String(dispute.status)) ? (
                  <button
                    type="button"
                    className="sd-btn sd-btn--primary sd-btn--block"
                    disabled={savingStatus}
                    onClick={onReopen}
                  >
                    Reopen Dispute
                  </button>
                ) : null}
              </div>
            )}
            <ol className="sd-status-flow">
              {SUPPORT_STATUS_OPTIONS
                .filter((item) => item.value !== "reopened")
                .map((item) => (
                  <li
                    key={item.value}
                    className={item.value === dispute.status ? "is-current" : ""}
                  >
                    <i aria-hidden />
                    <span>{item.label}</span>
                    {item.value === dispute.status ? <small>Current status</small> : null}
                  </li>
                ))}
            </ol>
          </section>

          <section className="sd-card sd-attachments-card">
            <h2><LuPaperclip aria-hidden /> Attachments ({attachments.length})</h2>
            {attachments.length ? (
              <div className="sd-attachment-list">
                {attachments.map((item) => (
                  <a
                    key={item.id}
                    href={resolvePublicFileUrl(item.path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LuFileText aria-hidden />
                    <span>{item.name}</span>
                    <LuDownload aria-hidden />
                  </a>
                ))}
              </div>
            ) : (
              <p className="sd-card-empty">No attachments added.</p>
            )}
          </section>

          <section className="sd-card sd-quick-actions">
            <h2>Quick Actions</h2>
            <button type="button" className="is-note" onClick={beginInternalNote}>
              <LuLock aria-hidden /> Add Internal Note
            </button>
            {permissions.canSendPublicMessages ? (
              <button type="button" className="is-info" onClick={requestMoreInfo}>
                <LuMailQuestion aria-hidden /> Request More Info
              </button>
            ) : null}
            {permissions.canUpdateStatus ? (
              <button type="button" className="is-danger" onClick={escalateDispute}>
                <LuTriangleAlert aria-hidden /> Escalate Dispute
              </button>
            ) : null}
          </section>

          <section className="sd-card sd-help-card">
            <LuInfo aria-hidden />
            <div>
              <h2>Need Help?</h2>
              <p>Check our help center or contact your team lead for assistance.</p>
              <Link to="/help-center">Visit Help Center</Link>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
